<template>
  <section class="w-full px-4 md:px-8 py-8 bg-[#f7f8fa] min-h-screen">
    <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-3">
      <div>
        <h1 class="text-3xl font-bold text-[#152a34]">Google Ads — painel interno</h1>
        <p class="text-sm text-gray-600 mt-1">
          Dados sincronizados da API (campanhas, termos, métricas, leilão quando disponível).
        </p>
        <router-link
          to="/kpis-internos"
          class="inline-block mt-2 text-sm text-[#b99a52] hover:text-[#a08840] font-medium"
        >
          Voltar para KPIs internos
        </router-link>
      </div>
      <div class="flex flex-col sm:flex-row sm:items-center gap-3">
        <div class="bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-sm">
          <p class="text-xs text-gray-500">Gerado em</p>
          <p class="text-sm font-medium text-[#152a34]">{{ generatedAt || '-' }}</p>
        </div>
        <button
          type="button"
          @click="reloadAll"
          class="bg-[#152a34] hover:bg-[#1d3b49] text-white px-5 py-2.5 rounded-lg transition w-full sm:w-auto"
        >
          Atualizar
        </button>
      </div>
    </div>

    <p v-if="loading" class="text-gray-600">Carregando...</p>
    <p v-if="error" class="text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
      {{ error }}
    </p>

    <div v-if="counts" class="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-3 mb-6">
      <article
        v-for="card in countCards"
        :key="card.key"
        class="bg-white rounded-xl border border-gray-200 p-4 shadow-sm"
      >
        <p class="text-xs text-gray-500">{{ card.label }}</p>
        <p class="text-xl font-semibold text-[#152a34]">{{ card.value }}</p>
      </article>
    </div>

    <div class="flex flex-wrap gap-2 mb-4 border-b border-gray-200 pb-2">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        type="button"
        @click="setTab(tab.id)"
        :class="[
          'px-3 py-1.5 rounded-lg text-sm transition',
          activeTab === tab.id
            ? 'bg-[#152a34] text-white'
            : 'bg-white border border-gray-200 text-[#152a34] hover:border-[#b99a52]',
        ]"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- Resumo: últimas execuções -->
    <div v-if="activeTab === 'runs'" class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <h2 class="font-semibold text-[#152a34] mb-3">Últimas sincronizações</h2>
      <div class="overflow-auto max-h-[480px]">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left border-b">
              <th class="py-2 pr-2">ID</th>
              <th class="py-2 pr-2">Status</th>
              <th class="py-2 pr-2">Início</th>
              <th class="py-2 pr-2">Fim</th>
              <th class="py-2 pr-2">Erro</th>
              <th class="py-2">Stats</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in syncRuns" :key="r.id" class="border-b align-top">
              <td class="py-2 pr-2">{{ r.id }}</td>
              <td class="py-2 pr-2">{{ r.status }}</td>
              <td class="py-2 pr-2">{{ formatDateTime(r.started_at) }}</td>
              <td class="py-2 pr-2">{{ formatDateTime(r.finished_at) }}</td>
              <td class="py-2 pr-2 max-w-xs truncate" :title="r.error_message || ''">
                {{ r.error_message || '-' }}
              </td>
              <td class="py-2 text-xs font-mono break-all">{{ formatStats(r.stats_json) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Campanhas -->
    <div v-if="activeTab === 'campaigns'" class="space-y-3">
      <div class="flex flex-wrap gap-2 items-center">
        <input
          v-model="filters.campaignsQ"
          type="text"
          placeholder="Buscar nome ou ID da campanha..."
          class="w-full md:w-72 border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#b99a52]"
          @keyup.enter="loadCampaigns"
        />
        <button
          type="button"
          @click="loadCampaigns"
          class="bg-[#b99a52] hover:bg-[#a08840] text-white px-4 py-2 rounded-lg text-sm"
        >
          Filtrar
        </button>
      </div>
      <div class="overflow-auto bg-white rounded-xl border border-gray-200 shadow-sm max-h-[560px]">
        <table class="w-full text-sm">
          <thead class="sticky top-0 bg-gray-50">
            <tr class="text-left border-b">
              <th class="py-2 px-3">Campanha</th>
              <th class="py-2 px-3">ID</th>
              <th class="py-2 px-3">Status</th>
              <th class="py-2 px-3">Canal</th>
              <th class="py-2 px-3">Orçamento (micros)</th>
              <th class="py-2 px-3">Capturado</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in campaignsRows" :key="row.id" class="border-b">
              <td class="py-2 px-3 max-w-xs truncate" :title="row.name">{{ row.name }}</td>
              <td class="py-2 px-3">{{ row.campaign_id }}</td>
              <td class="py-2 px-3">{{ row.status }}</td>
              <td class="py-2 px-3">{{ row.advertising_channel_type }}</td>
              <td class="py-2 px-3">{{ row.budget_amount_micros }}</td>
              <td class="py-2 px-3">{{ formatDateTime(row.captured_at) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Termos de pesquisa -->
    <div v-if="activeTab === 'terms'" class="space-y-3">
      <div class="flex flex-wrap gap-2 items-center">
        <input
          v-model="filters.termsQ"
          type="text"
          placeholder="Termo..."
          class="border border-gray-200 rounded-xl px-3 py-2 text-sm w-48 focus:ring-2 focus:ring-[#b99a52] focus:outline-none"
        />
        <input
          v-model="filters.termsCampaign"
          type="text"
          placeholder="ID campanha"
          class="border border-gray-200 rounded-xl px-3 py-2 text-sm w-36 focus:ring-2 focus:ring-[#b99a52] focus:outline-none"
        />
        <input
          v-model="filters.termsFrom"
          type="date"
          class="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#b99a52] focus:outline-none"
        />
        <input
          v-model="filters.termsTo"
          type="date"
          class="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#b99a52] focus:outline-none"
        />
        <button
          type="button"
          @click="loadSearchTerms"
          class="bg-[#b99a52] hover:bg-[#a08840] text-white px-4 py-2 rounded-lg text-sm"
        >
          Filtrar
        </button>
      </div>
      <div class="overflow-auto bg-white rounded-xl border border-gray-200 shadow-sm max-h-[560px]">
        <table class="w-full text-sm">
          <thead class="sticky top-0 bg-gray-50">
            <tr class="text-left border-b">
              <th class="py-2 px-3">Data</th>
              <th class="py-2 px-3">Termo</th>
              <th class="py-2 px-3">Camp.</th>
              <th class="py-2 px-3">Grp</th>
              <th class="py-2 px-3">Imp</th>
              <th class="py-2 px-3">Cli</th>
              <th class="py-2 px-3">Custo R$</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in termsRows" :key="row.id" class="border-b">
              <td class="py-2 px-3">{{ formatDateOnly(row.stat_date) }}</td>
              <td class="py-2 px-3 max-w-xs truncate" :title="row.search_term">{{ row.search_term }}</td>
              <td class="py-2 px-3">{{ row.campaign_id }}</td>
              <td class="py-2 px-3">{{ row.ad_group_id }}</td>
              <td class="py-2 px-3">{{ row.impressions }}</td>
              <td class="py-2 px-3">{{ row.clicks }}</td>
              <td class="py-2 px-3">{{ microsToReais(row.cost_micros) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Palavras-chave -->
    <div v-if="activeTab === 'keywords'" class="space-y-3">
      <div class="flex flex-wrap gap-2">
        <input
          v-model="filters.kwQ"
          type="text"
          placeholder="Palavra-chave..."
          class="border border-gray-200 rounded-xl px-3 py-2 text-sm w-64 focus:ring-2 focus:ring-[#b99a52] focus:outline-none"
          @keyup.enter="loadKeywords"
        />
        <button type="button" @click="loadKeywords" class="bg-[#b99a52] text-white px-4 py-2 rounded-lg text-sm">
          Filtrar
        </button>
      </div>
      <div class="overflow-auto bg-white rounded-xl border max-h-[560px]">
        <table class="w-full text-sm">
          <thead class="sticky top-0 bg-gray-50">
            <tr class="text-left border-b">
              <th class="py-2 px-3">Keyword</th>
              <th class="py-2 px-3">Match</th>
              <th class="py-2 px-3">QS</th>
              <th class="py-2 px-3">Camp.</th>
              <th class="py-2 px-3">Grupo</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in kwRows" :key="row.id" class="border-b">
              <td class="py-2 px-3">{{ row.keyword_text }}</td>
              <td class="py-2 px-3">{{ row.match_type }}</td>
              <td class="py-2 px-3">{{ row.quality_score }}</td>
              <td class="py-2 px-3">{{ row.campaign_id }}</td>
              <td class="py-2 px-3">{{ row.ad_group_id }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Negativas -->
    <div v-if="activeTab === 'negatives'" class="space-y-3">
      <div class="flex flex-wrap gap-2">
        <input
          v-model="filters.negQ"
          type="text"
          placeholder="Negativa..."
          class="border border-gray-200 rounded-xl px-3 py-2 text-sm w-64 focus:ring-2 focus:ring-[#b99a52] focus:outline-none"
        />
        <select
          v-model="filters.negScope"
          class="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white"
        >
          <option value="">Todos escopos</option>
          <option value="campaign">Campanha</option>
          <option value="ad_group">Grupo de anúncios</option>
        </select>
        <button type="button" @click="loadNegatives" class="bg-[#b99a52] text-white px-4 py-2 rounded-lg text-sm">
          Filtrar
        </button>
      </div>
      <div class="overflow-auto bg-white rounded-xl border max-h-[560px]">
        <table class="w-full text-sm">
          <thead class="sticky top-0 bg-gray-50">
            <tr class="text-left border-b">
              <th class="py-2 px-3">Escopo</th>
              <th class="py-2 px-3">Texto</th>
              <th class="py-2 px-3">Match</th>
              <th class="py-2 px-3">Camp.</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in negRows" :key="row.id" class="border-b">
              <td class="py-2 px-3">{{ row.scope }}</td>
              <td class="py-2 px-3">{{ row.keyword_text }}</td>
              <td class="py-2 px-3">{{ row.match_type }}</td>
              <td class="py-2 px-3">{{ row.campaign_id }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Anúncios -->
    <div v-if="activeTab === 'ads'" class="space-y-3">
      <div class="flex flex-wrap gap-2">
        <input
          v-model="filters.adsQ"
          type="text"
          placeholder="ID anúncio ou resource..."
          class="border border-gray-200 rounded-xl px-3 py-2 text-sm w-64 focus:ring-2 focus:ring-[#b99a52] focus:outline-none"
        />
        <button type="button" @click="loadAds" class="bg-[#b99a52] text-white px-4 py-2 rounded-lg text-sm">
          Filtrar
        </button>
      </div>
      <div class="space-y-4 max-h-[640px] overflow-auto">
        <article
          v-for="row in adsRows"
          :key="row.id"
          class="bg-white rounded-xl border border-gray-200 p-4 shadow-sm text-sm"
        >
          <p class="font-semibold text-[#152a34]">
            {{ row.ad_type }} — {{ row.status }} — ad {{ row.ad_id }}
          </p>
          <p class="text-gray-600 text-xs">Campanha {{ row.campaign_id }} · Grupo {{ row.ad_group_id }}</p>
          <p class="mt-2 text-xs font-medium text-gray-700">Títulos (RSA)</p>
          <pre class="text-xs bg-gray-50 p-2 rounded-lg overflow-auto max-h-32">{{ prettyJson(row.rsa_headlines_json) }}</pre>
          <p class="mt-2 text-xs font-medium text-gray-700">Descrições</p>
          <pre class="text-xs bg-gray-50 p-2 rounded-lg overflow-auto max-h-32">{{ prettyJson(row.rsa_descriptions_json) }}</pre>
        </article>
      </div>
    </div>

    <!-- Métricas diárias -->
    <div v-if="activeTab === 'metrics'" class="space-y-3">
      <div class="flex flex-wrap gap-2">
        <input
          v-model="filters.metCampaign"
          type="text"
          placeholder="ID campanha"
          class="border border-gray-200 rounded-xl px-3 py-2 text-sm w-40 focus:ring-2 focus:ring-[#b99a52] focus:outline-none"
        />
        <input v-model="filters.metFrom" type="date" class="border border-gray-200 rounded-xl px-3 py-2 text-sm" />
        <input v-model="filters.metTo" type="date" class="border border-gray-200 rounded-xl px-3 py-2 text-sm" />
        <button type="button" @click="loadMetrics" class="bg-[#b99a52] text-white px-4 py-2 rounded-lg text-sm">
          Filtrar
        </button>
      </div>
      <div class="overflow-auto bg-white rounded-xl border max-h-[560px]">
        <table class="w-full text-sm">
          <thead class="sticky top-0 bg-gray-50">
            <tr class="text-left border-b">
              <th class="py-2 px-3">Data</th>
              <th class="py-2 px-3">Camp.</th>
              <th class="py-2 px-3">Imp</th>
              <th class="py-2 px-3">Cli</th>
              <th class="py-2 px-3">Custo R$</th>
              <th class="py-2 px-3">IS pesquisa</th>
              <th class="py-2 px-3">Perda rank</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in metRows" :key="row.id" class="border-b">
              <td class="py-2 px-3">{{ formatDateOnly(row.stat_date) }}</td>
              <td class="py-2 px-3">{{ row.campaign_id }}</td>
              <td class="py-2 px-3">{{ row.impressions }}</td>
              <td class="py-2 px-3">{{ row.clicks }}</td>
              <td class="py-2 px-3">{{ microsToReais(row.cost_micros) }}</td>
              <td class="py-2 px-3">{{ formatPct(row.search_impression_share) }}</td>
              <td class="py-2 px-3">{{ formatPct(row.search_rank_lost_impression_share) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Leilão / auction insights -->
    <div v-if="activeTab === 'auction'" class="space-y-3">
      <p class="text-xs text-gray-600">
        Dados equivalentes à área “Informações do leilão” quando a API permitir (pode exigir allowlist na conta).
      </p>
      <div class="flex flex-wrap gap-2">
        <input
          v-model="filters.aucQ"
          type="text"
          placeholder="Domínio concorrente..."
          class="border border-gray-200 rounded-xl px-3 py-2 text-sm w-56 focus:ring-2 focus:ring-[#b99a52] focus:outline-none"
        />
        <input
          v-model="filters.aucCampaign"
          type="text"
          placeholder="ID campanha"
          class="border border-gray-200 rounded-xl px-3 py-2 text-sm w-36"
        />
        <input v-model="filters.aucFrom" type="date" class="border border-gray-200 rounded-xl px-3 py-2 text-sm" />
        <input v-model="filters.aucTo" type="date" class="border border-gray-200 rounded-xl px-3 py-2 text-sm" />
        <button type="button" @click="loadAuction" class="bg-[#b99a52] text-white px-4 py-2 rounded-lg text-sm">
          Filtrar
        </button>
      </div>
      <div class="overflow-auto bg-white rounded-xl border max-h-[560px]">
        <table class="w-full text-sm">
          <thead class="sticky top-0 bg-gray-50">
            <tr class="text-left border-b">
              <th class="py-2 px-3">Data</th>
              <th class="py-2 px-3">Camp.</th>
              <th class="py-2 px-3">Concorrente</th>
              <th class="py-2 px-3">IS</th>
              <th class="py-2 px-3">Sobrepor</th>
              <th class="py-2 px-3">Acima</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in aucRows" :key="row.id" class="border-b">
              <td class="py-2 px-3">{{ formatDateOnly(row.stat_date) }}</td>
              <td class="py-2 px-3">{{ row.campaign_id }}</td>
              <td class="py-2 px-3 max-w-[200px] truncate" :title="row.competitor_domain">
                {{ row.competitor_domain }}
              </td>
              <td class="py-2 px-3">{{ formatPct(row.auction_insight_search_impression_share) }}</td>
              <td class="py-2 px-3">{{ formatPct(row.auction_insight_search_overlap_rate) }}</td>
              <td class="py-2 px-3">{{ formatPct(row.auction_insight_search_position_above_rate) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script>
import {
  getInternalGoogleAdsOverview,
  getInternalGoogleAdsCampaigns,
  getInternalGoogleAdsSearchTerms,
  getInternalGoogleAdsKeywords,
  getInternalGoogleAdsNegatives,
  getInternalGoogleAdsAds,
  getInternalGoogleAdsMetricsDaily,
  getInternalGoogleAdsAuctionInsights,
} from '@/services/api';

export default {
  name: 'InternalGoogleAdsDashboard',
  data() {
    return {
      loading: false,
      error: '',
      generatedAt: '',
      counts: null,
      syncRuns: [],
      activeTab: 'runs',
      campaignsRows: [],
      termsRows: [],
      kwRows: [],
      negRows: [],
      adsRows: [],
      metRows: [],
      aucRows: [],
      filters: {
        campaignsQ: '',
        termsQ: '',
        termsCampaign: '',
        termsFrom: '',
        termsTo: '',
        kwQ: '',
        negQ: '',
        negScope: '',
        adsQ: '',
        metCampaign: '',
        metFrom: '',
        metTo: '',
        aucQ: '',
        aucCampaign: '',
        aucFrom: '',
        aucTo: '',
      },
      tabs: [
        { id: 'runs', label: 'Sincronizações' },
        { id: 'campaigns', label: 'Campanhas' },
        { id: 'terms', label: 'Termos de pesquisa' },
        { id: 'keywords', label: 'Palavras-chave' },
        { id: 'negatives', label: 'Negativas' },
        { id: 'ads', label: 'Anúncios RSA' },
        { id: 'metrics', label: 'Métricas diárias' },
        { id: 'auction', label: 'Leilão (insights)' },
      ],
    };
  },
  computed: {
    countCards() {
      const c = this.counts || {};
      return [
        { key: 'c', label: 'Campanhas', value: c.campaigns ?? 0 },
        { key: 'ag', label: 'Grupos', value: c.ad_groups ?? 0 },
        { key: 'kw', label: 'Keywords', value: c.keywords ?? 0 },
        { key: 'neg', label: 'Negativas', value: c.negative_keywords ?? 0 },
        { key: 'ad', label: 'Anúncios', value: c.ads ?? 0 },
        { key: 'st', label: 'Termos', value: c.search_terms ?? 0 },
        { key: 'md', label: 'Métricas/dia', value: c.metrics_daily ?? 0 },
        { key: 'ai', label: 'Leilão', value: c.auction_insights ?? 0 },
      ];
    },
  },
  methods: {
    formatDateTime(value) {
      if (!value) return '-';
      return new Date(value).toLocaleString('pt-BR');
    },
    formatDateOnly(value) {
      if (!value) return '-';
      const s = String(value).trim();
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:T|$)/);
      if (m) {
        const y = Number(m[1]);
        const mo = Number(m[2]) - 1;
        const d = Number(m[3]);
        return new Date(y, mo, d).toLocaleDateString('pt-BR');
      }
      return new Date(value).toLocaleDateString('pt-BR');
    },
    microsToReais(m) {
      const n = Number(m);
      if (!Number.isFinite(n)) return '-';
      return (n / 1_000_000).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    formatPct(v) {
      if (v === null || v === undefined || v === '') return '-';
      const n = Number(v);
      if (!Number.isFinite(n)) return '-';
      const pct = n > 1 && n <= 100 ? n : n * 100;
      return `${pct.toFixed(1)}%`;
    },
    formatStats(raw) {
      if (raw == null) return '-';
      if (typeof raw === 'object') {
        try {
          return JSON.stringify(raw);
        } catch {
          return '-';
        }
      }
      return String(raw);
    },
    prettyJson(raw) {
      if (raw == null) return '-';
      try {
        const v = typeof raw === 'string' ? JSON.parse(raw) : raw;
        return JSON.stringify(v, null, 2);
      } catch {
        return String(raw);
      }
    },
    setTab(id) {
      this.activeTab = id;
      this.error = '';
      if (id === 'campaigns') this.loadCampaigns();
      else if (id === 'terms') this.loadSearchTerms();
      else if (id === 'keywords') this.loadKeywords();
      else if (id === 'negatives') this.loadNegatives();
      else if (id === 'ads') this.loadAds();
      else if (id === 'metrics') this.loadMetrics();
      else if (id === 'auction') this.loadAuction();
    },
    async loadOverview() {
      const { data } = await getInternalGoogleAdsOverview();
      this.counts = data?.counts || null;
      this.syncRuns = data?.sync_runs || [];
      this.generatedAt = data?.generated_at ? this.formatDateTime(data.generated_at) : '';
    },
    async loadCampaigns() {
      const { data } = await getInternalGoogleAdsCampaigns({
        q: this.filters.campaignsQ || undefined,
        limit: 80,
      });
      this.campaignsRows = data?.rows || [];
    },
    async loadSearchTerms() {
      const { data } = await getInternalGoogleAdsSearchTerms({
        q: this.filters.termsQ || undefined,
        campaign_id: this.filters.termsCampaign || undefined,
        date_from: this.filters.termsFrom || undefined,
        date_to: this.filters.termsTo || undefined,
        limit: 200,
      });
      this.termsRows = data?.rows || [];
    },
    async loadKeywords() {
      const { data } = await getInternalGoogleAdsKeywords({
        q: this.filters.kwQ || undefined,
        limit: 120,
      });
      this.kwRows = data?.rows || [];
    },
    async loadNegatives() {
      const { data } = await getInternalGoogleAdsNegatives({
        q: this.filters.negQ || undefined,
        scope: this.filters.negScope || undefined,
        limit: 120,
      });
      this.negRows = data?.rows || [];
    },
    async loadAds() {
      const { data } = await getInternalGoogleAdsAds({
        q: this.filters.adsQ || undefined,
        limit: 40,
      });
      this.adsRows = data?.rows || [];
    },
    async loadMetrics() {
      const { data } = await getInternalGoogleAdsMetricsDaily({
        campaign_id: this.filters.metCampaign || undefined,
        date_from: this.filters.metFrom || undefined,
        date_to: this.filters.metTo || undefined,
        limit: 200,
      });
      this.metRows = data?.rows || [];
    },
    async loadAuction() {
      const { data } = await getInternalGoogleAdsAuctionInsights({
        q: this.filters.aucQ || undefined,
        campaign_id: this.filters.aucCampaign || undefined,
        date_from: this.filters.aucFrom || undefined,
        date_to: this.filters.aucTo || undefined,
        limit: 200,
      });
      this.aucRows = data?.rows || [];
    },
    async reloadAll() {
      this.loading = true;
      this.error = '';
      try {
        await this.loadOverview();
        if (this.activeTab === 'runs') {
          /* já em overview */
        } else if (this.activeTab === 'campaigns') await this.loadCampaigns();
        else if (this.activeTab === 'terms') await this.loadSearchTerms();
        else if (this.activeTab === 'keywords') await this.loadKeywords();
        else if (this.activeTab === 'negatives') await this.loadNegatives();
        else if (this.activeTab === 'ads') await this.loadAds();
        else if (this.activeTab === 'metrics') await this.loadMetrics();
        else if (this.activeTab === 'auction') await this.loadAuction();
      } catch (err) {
        this.error = err?.response?.data?.error || 'Falha ao carregar dados internos Google Ads.';
      } finally {
        this.loading = false;
      }
    },
  },
  async mounted() {
    await this.reloadAll();
  },
};
</script>
